// Single source of truth for ontology-driven lifecycle status on the client.
// Status is an enum property attached per interface/type. Each kind maps to its
// own lifecycle so the state machine fits the kind. The stored value lives in
// objects.properties_json.lifecycle (mirrors how `progress` is persisted).

export type LifecycleStatus = string;

export type LifecycleInterface =
  | "source"
  | "task"
  | "exercise"
  | "practice"
  | "note"
  | "quote"
  | "reflection"
  | "critique"
  | "expansion";

export type LifecycleOption = {
  slug: string;
  label: string;
  labelVi: string;
  tone: string; // tailwind classes for badge/chip
  dotColor: string; // hex for graph node fill + legend dots
};

const SOURCE_OPTIONS: LifecycleOption[] = [
  { slug: "not_started", label: "Not started", labelVi: "Chưa bắt đầu", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "in_progress", label: "In progress", labelVi: "Đang học", tone: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "#f59e0b" },
  { slug: "completed", label: "Completed", labelVi: "Hoàn tất", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" },
  { slug: "archived_state", label: "Archived", labelVi: "Lưu trữ", tone: "bg-slate-100 text-slate-500 border-slate-200", dotColor: "#94a3b8" }
];

// Tasks: classic to-do workflow.
const TASK_OPTIONS: LifecycleOption[] = [
  { slug: "todo", label: "To do", labelVi: "Cần làm", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "doing", label: "Doing", labelVi: "Đang làm", tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { slug: "done", label: "Done", labelVi: "Xong", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" },
  { slug: "cancelled", label: "Cancelled", labelVi: "Đã huỷ", tone: "bg-rose-100 text-rose-600 border-rose-200", dotColor: "#f43f5e" }
];

// Exercises: attempt then solve.
const EXERCISE_OPTIONS: LifecycleOption[] = [
  { slug: "todo", label: "To do", labelVi: "Cần làm", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "attempted", label: "Attempted", labelVi: "Đã thử", tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { slug: "solved", label: "Solved", labelVi: "Đã giải", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

// Practice: spaced-repetition / skill mastery.
const PRACTICE_OPTIONS: LifecycleOption[] = [
  { slug: "learning", label: "Learning", labelVi: "Đang học", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "reviewing", label: "Reviewing", labelVi: "Đang ôn", tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { slug: "mastered", label: "Mastered", labelVi: "Thành thạo", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

// Notes: digital-garden maturity (matches the app metaphor).
const NOTE_OPTIONS: LifecycleOption[] = [
  { slug: "seedling", label: "🌱 Seedling", labelVi: "🌱 Hạt giống", tone: "bg-lime-100 text-lime-700 border-lime-200", dotColor: "#84cc16" },
  { slug: "budding", label: "🌿 Budding", labelVi: "🌿 Nảy mầm", tone: "bg-green-100 text-green-700 border-green-200", dotColor: "#22c55e" },
  { slug: "evergreen", label: "🌳 Evergreen", labelVi: "🌳 Lâu năm", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#047857" }
];

// Quotes: reference excerpts get processed, then applied.
const QUOTE_OPTIONS: LifecycleOption[] = [
  { slug: "captured", label: "Captured", labelVi: "Đã lưu", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "processed", label: "Processed", labelVi: "Đã xử lý", tone: "bg-violet-100 text-violet-700 border-violet-200", dotColor: "#8b5cf6" },
  { slug: "applied", label: "Applied", labelVi: "Đã áp dụng", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

// Reflections: personal thinking, revisited over time.
const REFLECTION_OPTIONS: LifecycleOption[] = [
  { slug: "draft", label: "Draft", labelVi: "Nháp", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "revisited", label: "Revisited", labelVi: "Đã xem lại", tone: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "#f59e0b" },
  { slug: "resolved", label: "Resolved", labelVi: "Đã chốt", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

// Critique: a rebuttal raised, argued, then settled.
const CRITIQUE_OPTIONS: LifecycleOption[] = [
  { slug: "open", label: "Open", labelVi: "Mới nêu", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "arguing", label: "Arguing", labelVi: "Đang phản biện", tone: "bg-rose-100 text-rose-700 border-rose-200", dotColor: "#f43f5e" },
  { slug: "settled", label: "Settled", labelVi: "Đã ngã ngũ", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

// Expansion: an outline that gets developed, then integrated into the knowledge base.
const EXPANSION_OPTIONS: LifecycleOption[] = [
  { slug: "outline", label: "Outline", labelVi: "Dàn ý", tone: "bg-stone-100 text-stone-600 border-stone-200", dotColor: "#a8a29e" },
  { slug: "expanding", label: "Expanding", labelVi: "Đang mở rộng", tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { slug: "integrated", label: "Integrated", labelVi: "Đã tích hợp", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "#10b981" }
];

export const LIFECYCLE_OPTIONS: Record<LifecycleInterface, LifecycleOption[]> = {
  source: SOURCE_OPTIONS,
  task: TASK_OPTIONS,
  exercise: EXERCISE_OPTIONS,
  practice: PRACTICE_OPTIONS,
  note: NOTE_OPTIONS,
  quote: QUOTE_OPTIONS,
  reflection: REFLECTION_OPTIONS,
  critique: CRITIQUE_OPTIONS,
  expansion: EXPANSION_OPTIONS
};

const KIND_TO_INTERFACE: Record<string, LifecycleInterface> = {
  task: "task",
  exercise: "exercise",
  practice: "practice",
  note: "note",
  quote: "quote",
  reflection: "reflection",
  critique: "critique",
  expansion: "expansion"
};

export function interfaceForKind(kind: string): LifecycleInterface {
  return KIND_TO_INTERFACE[kind] ?? "source";
}

export function statusOptionsForKind(kind: string): LifecycleOption[] {
  return LIFECYCLE_OPTIONS[interfaceForKind(kind)];
}

export function defaultStatusForKind(kind: string): LifecycleStatus {
  return statusOptionsForKind(kind)[0]?.slug ?? "not_started";
}

// Deterministic palette for custom (user-added) statuses that aren't in the seeded enum,
// so the same custom slug always gets the same badge/dot color across nodes.
const CUSTOM_PALETTE: Array<{ tone: string; dotColor: string }> = [
  { tone: "bg-rose-100 text-rose-700 border-rose-200", dotColor: "#f43f5e" },
  { tone: "bg-orange-100 text-orange-700 border-orange-200", dotColor: "#f97316" },
  { tone: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "#f59e0b" },
  { tone: "bg-teal-100 text-teal-700 border-teal-200", dotColor: "#14b8a6" },
  { tone: "bg-sky-100 text-sky-700 border-sky-200", dotColor: "#0ea5e9" },
  { tone: "bg-indigo-100 text-indigo-700 border-indigo-200", dotColor: "#6366f1" },
  { tone: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200", dotColor: "#d946ef" }
];

function hashSlug(slug: string): number {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Turn a free-typed status label into a stable slug (matches enum slug shape: a-z0-9_).
export function slugifyStatus(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

// Pretty fallback label for a custom slug ("on_hold" -> "On hold").
function humanizeSlug(slug: string): string {
  const words = slug.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function statusMeta(kind: string, slug?: string): LifecycleOption {
  const options = statusOptionsForKind(kind);
  const resolved = slug ?? defaultStatusForKind(kind);
  const builtIn = options.find((option) => option.slug === resolved);
  if (builtIn) {
    return builtIn;
  }
  // Custom status: deterministic color + humanized label so it renders nicely.
  const palette = CUSTOM_PALETTE[hashSlug(resolved) % CUSTOM_PALETTE.length];
  return { slug: resolved, label: humanizeSlug(resolved), labelVi: humanizeSlug(resolved), tone: palette.tone, dotColor: palette.dotColor };
}

export function statusColor(kind: string, slug?: string): string {
  return statusMeta(kind, slug).dotColor;
}
